import React, { Fragment, type ReactNode } from 'react'

import {
  type ShipplayHowItWorksStepContent,
  type ShipplayHowItWorksStepsContent,
} from '@/app/how-it-works/shipplay.page.content'

import { AirplaneIcon } from '../icons/Airplane'
import { CalendarIcon } from '../icons/Calendar'
import { LuggageTagIcon } from '../icons/LuggageTag'
import { Path } from '../icons/Path'
import { ShippingTruckIcon } from '../icons/ShippingTruck'
import { Button } from '../ui/Button'
import { cn } from '../ui/cn'
import { SplitBlock } from '../ui_patterns/SplitBlock'

interface HowItWorksStepsProps {
  content: ShipplayHowItWorksStepsContent
}

const renderIcon = (icon: ShipplayHowItWorksStepContent['icon']): ReactNode => {
  switch (icon) {
    case 'calendar':
      return <CalendarIcon className="snplay:size-20" />
    case 'luggage-tag':
      return <LuggageTagIcon className="snplay:size-20" />
    case 'shipping-truck':
      return <ShippingTruckIcon className="snplay:size-20" />
    case 'airplane':
      return <AirplaneIcon className="snplay:size-20" />
  }
}

export const HowItWorksSteps = ({ content }: HowItWorksStepsProps): React.ReactElement => {
  // `isolate` establishes a local stacking context so the Connector's `-z-1`
  // sits behind sibling SplitBlocks (the desired look) without escaping to
  // Kettle's `<main class="bg-white">` where a raw -z-1 would paint behind
  // main's background and never show.
  return (
    <div className="snplay:isolate">
      {content.steps.map((step, index) => (
        <Fragment key={index}>
          <SplitBlock
            className={step.className}
            imgUrl={step.imgUrl}
            imgAlt={step.imgAlt}
            imgSide={step.imgSide}
          >
            <div className="snplay:space-y-8">
              {renderIcon(step.icon)}
              <h1 className="snplay:text-4xl snplay:font-bold">
                {step.title.prefix}
                <span className="snplay:text-primaryDark">{step.title.emphasis}</span>
                {step.title.suffix}
              </h1>
              <div className="snplay:space-y-4 snplay:text-base snplay:text-ui-secondary">
                {step.body}
              </div>
              {step.button.to !== undefined ? (
                <Button variant="primary" size="large" to={step.button.to} className="snplay:w-fit">
                  {step.button.text}
                </Button>
              ) : (
                <Button variant="primary" size="large" className="snplay:w-fit">
                  {step.button.text}
                </Button>
              )}
            </div>
          </SplitBlock>
          {step.connectorAfter !== null && step.connectorAfter !== undefined && (
            <Connector className={step.connectorAfter.className} />
          )}
        </Fragment>
      ))}
    </div>
  )
}

interface ConnectorProps {
  className?: string
}

const Connector = ({ className }: ConnectorProps): React.ReactElement => {
  return (
    <div
      className={cn(
        'snplay-bounds snplay:relative snplay:-z-1',
        'snplay:min-h-[6rem] snplay:md:min-h-[12rem]',
        className,
      )}
    >
      {/* Mobile: straight vertical dashed line connecting the stacked step images.
          The Path SVG below is a curved right→left path that only makes sense on
          the alternating desktop layout, hence its max-md:hidden. */}
      <div
        aria-hidden
        className={cn(
          'snplay:absolute snplay:-top-10 snplay:-bottom-10',
          'snplay:left-1/2 snplay:w-0 snplay:-translate-x-1/2',
          'snplay:border-l-2 snplay:border-dashed snplay:border-primary snplay:md:hidden',
        )}
      />
      {/* Desktop curved dashed path. Painted below sibling SplitBlocks via
          the isolate stacking context established by the HowItWorksSteps
          wrapper — matches playground's arc-behind-images look while still
          appearing above main's white background. */}
      <Path
        className={cn(
          'snplay:absolute snplay:-top-10 snplay:-bottom-10',
          'snplay:left-1/2 snplay:w-1/2 snplay:-translate-x-1/2',
          'snplay:stroke-primary snplay:max-md:hidden',
        )}
      />
    </div>
  )
}
